/**
 * SocialLinks — exibe as redes sociais configuradas no painel de controle.
 *
 * Consome o contexto SocialNetworksProvider (ou Redux diretamente se o
 * Provider estiver mais acima na árvore).
 * Usa os ícones SVG disponíveis em src/icons/; redes sem ícone específico
 * recebem um link de texto acessível.
 */
import cx from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { useSocialNetworksContext } from '../SocialNetworksProvider/SocialNetworksProvider';

import facebookSVG from '../../icons/facebook.svg';
import flickrSVG from '../../icons/flickr.svg';
import instagramSVG from '../../icons/instagram.svg';
import linkedinSVG from '../../icons/linkedin.svg';
import whatsappSVG from '../../icons/whatsapp.svg';
import xTwitterSVG from '../../icons/x-twitter.svg';
import youtubeSVG from '../../icons/youtube.svg';

const messages = defineMessages({
  followOn: {
    id: 'social.followOn',
    defaultMessage: 'Follow us on {network}',
  },
});

/** Mapa de redes para seus ícones SVG locais. */
export const NETWORK_ICONS: Record<string, string> = {
  Facebook: facebookSVG,
  Flickr: flickrSVG,
  Instagram: instagramSVG,
  Linkedin: linkedinSVG,
  Whatsapp: whatsappSVG,
  X: xTwitterSVG,
  Youtube: youtubeSVG,
};

export interface SocialLinksProps {
  className?: string;
  /** Tamanho dos ícones SVG (padrão: '24px') */
  iconSize?: string;
}

const SocialLinks = ({ className, iconSize = '24px' }: SocialLinksProps) => {
  const intl = useIntl();
  const { networks, loaded, error } = useSocialNetworksContext();

  if (!loaded || error || networks.length === 0) {
    return null;
  }

  return (
    <ul className={cx('social-links', className)}>
      {networks.map((network) => {
        const name = network.social_network;
        if (!name || !network.social_network_url) return null;

        const icon = NETWORK_ICONS[name];
        const label = intl.formatMessage(messages.followOn, { network: name });

        return (
          <li key={network['@id'] || name} className="social-links__item">
            <a
              href={network.social_network_url}
              className="social-links__link"
              aria-label={label}
              title={label}
              target="_blank"
              rel="noopener noreferrer"
            >
              {icon ? (
                <Icon name={icon} size={iconSize} />
              ) : (
                <span aria-hidden="true">{name}</span>
              )}
            </a>
          </li>
        );
      })}
    </ul>
  );
};

export default SocialLinks;
